import { graphql } from "gql.tada";

import { allow, deny, type PolicyResult } from "./policy";

/**
 * GraphQL スキーマから導出されたワークスペース内の組織ロールを表します。
 */
export type UserOrganizationRole = ReturnType<
  typeof graphql.scalar<"UserOrganizationRole">
>;

const ROLE_ORDER = [
  "GUEST",
  "RESTRICTED_MEMBER",
  "FULL_MEMBER",
  "ADMINISTRATOR",
  "OWNER",
] as const satisfies readonly UserOrganizationRole[];

/**
 * 指定されたロールの序列を返します。配列インデックスを利用することで、比較ロジックを簡潔に保ちます。
 */
export function rank(role: UserOrganizationRole): number {
  return ROLE_ORDER.indexOf(role);
}

/**
 * 自身のロールが対象ロール以上 (>=) であるかを判定します。
 */
export function isAtLeast(
  me: UserOrganizationRole,
  target: UserOrganizationRole,
): boolean {
  return rank(me) >= rank(target);
}

/**
 * 自身のロールが対象ロールより厳密に上位 (>) であるかを判定します。
 */
export function isStrictlyAbove(
  me: UserOrganizationRole,
  target: UserOrganizationRole,
): boolean {
  return rank(me) > rank(target);
}

/**
 * 必須ロール群のいずれかを保持しているか判定します。単一指定と配列指定の両方に対応します。
 */
export function hasAnyRole(
  me: UserOrganizationRole,
  require: UserOrganizationRole | UserOrganizationRole[],
): boolean {
  const needs = Array.isArray(require) ? require : [require];
  return needs.includes(me);
}

export type RoleRequirementOptions = {
  require?: UserOrganizationRole | UserOrganizationRole[];
  atLeast?: UserOrganizationRole;
  strictlyAbove?: UserOrganizationRole;
};

/**
 * ロール条件を評価し、満たさない場合は `deny()` を返します。
 */
export function evaluateRoleRequirement(
  me: UserOrganizationRole,
  options: RoleRequirementOptions,
): PolicyResult {
  if (options.require && !hasAnyRole(me, options.require)) {
    return deny();
  }
  if (options.atLeast && !isAtLeast(me, options.atLeast)) {
    return deny();
  }
  if (options.strictlyAbove && !isStrictlyAbove(me, options.strictlyAbove)) {
    return deny();
  }
  return allow();
}
